import {useEffect} from 'react'
import {_, t, useCurrentProjectState} from '../../lib'

import {
  selectBatchData,
  createDispatchActions,
  selectProjectAdherenceData,
  selectMethod,
  selectGarminDirectDataDigest,
  selectGarminConnectDataDigest,
  selectProjectDataDigest,
} from '../../store'

import {TaskStateType} from '../../model'
import {TaskType} from '../../shared/db'

export interface AttemptRequestParticipantStatusProps {
  clearCache?: boolean
  requestAdherence?: RequestAdherencePayload
  requestGarminDirectBbiDigest?: RequestGarminDataDigestPayload
  requestGarminConnectWearTime?: RequestGarminDataDigestPayload
  requestParticipantIssues?: boolean
  forceBatchFetch?: boolean
}

export interface RequestAdherencePayload {
  durationBeforeToday?: number
  yymmIndexToFetch?: number[]
}

export interface RequestGarminDataDigestPayload {
  yymmddIndexStart: number
  yymmddIndexEnd: number
}

export const AttemptRequestParticipantStatus = (props: AttemptRequestParticipantStatusProps) => {
  const {
    doREQUEST_BATCH_FETCH,
    doREQUEST_METHOD_FETCH,
    doREQUEST_PROJECT_DATA_DIGEST,
    doREQUEST_PROJECT_PARTICIPANT_LIST,
    doREQUEST_PROJECT_GARMIN_BBI_DATA_DIGEST_FETCH,
    doREQUEST_PROJECT_GARMIN_CONNECT_WEAR_TIME_DATA_FETCH,
    doREQUEST_PROJECT_PARTICIPANT_ISSUES_FETCH,
    doADHERENCE_DELETE,
  } = createDispatchActions()
  const {
    clearCache,
    requestAdherence,
    requestGarminDirectBbiDigest,
    requestGarminConnectWearTime,
    requestParticipantIssues,
    forceBatchFetch,
  } = props

  const {projectId, project} = useCurrentProjectState()
  const batchId = project?.batchList?.[0]?.id
  const methodId = project?.methodList?.[0].id || ''
  const batchData = selectBatchData()
  const method = selectMethod()

  const garminDeviceTask: TaskStateType | undefined = _.find(method?.taskList, {type: TaskType.GarminDevice})
  const garminDeviceEnable: boolean = garminDeviceTask ? true : false
  const garminConnectEnabled: boolean = method?.garminConnectEnable
  const dexcomDataEnabled: boolean = method.dexcomIntegrationId ? true : false
  const movesenseDeviceEnabled: boolean = method?.movesenseDeviceEnable

  const projectAdherence = selectProjectAdherenceData(projectId)
  const projectDataDigest = projectId ? selectProjectDataDigest()?.[projectId] : undefined
  const currentAdherence = projectAdherence?.participantAdherence
  const garminDirectDataDigest = selectGarminDirectDataDigest(projectId)
  const garminConnectDataDigest = selectGarminConnectDataDigest(projectId)

  useEffect(() => {
    if (!batchId) return
    if (!batchData[batchId] || forceBatchFetch) {
      doREQUEST_BATCH_FETCH({
        payload: {
          batchId,
        },
      })
    }
  }, [batchId, forceBatchFetch])

  useEffect(() => {
    if (methodId) {
      if (!method || method.id !== methodId) {
        doREQUEST_METHOD_FETCH({
          payload: {
            methodId,
          },
        })
      }
    }
  }, [methodId])

  useEffect(() => {
    if (projectId) {
      if (clearCache) {
        doADHERENCE_DELETE({projectId: projectId})
        doREQUEST_PROJECT_PARTICIPANT_LIST({
          payload: {
            projectId,
          },
        })
      } else if (!currentAdherence || Object.keys(currentAdherence).length == 0) {
        doREQUEST_PROJECT_PARTICIPANT_LIST({
          payload: {
            projectId,
          },
        })
      }
    }
  }, [projectId])

  useEffect(() => {
    if (requestAdherence) {
      // console.log(requestAdherence)
      if (project && method.id && projectAdherence?.participantAdherence) {
        const projectHasScheduleTask = project?.scheduleConfig ? true : false

        let yymmIndexList: number[] = []

        if (requestAdherence.durationBeforeToday) {
          const today = new Date()
          const queryStartDate = new Date(today)
          queryStartDate.setDate(queryStartDate.getDate() - requestAdherence.durationBeforeToday)
          const yymmIndexStart = convertDateToYYMMIndex(queryStartDate as Date)
          const yymmIndexEnd = convertDateToYYMMIndex(today as Date)
          yymmIndexList = [...new Set([yymmIndexStart, yymmIndexEnd])]
        } else if (requestAdherence.yymmIndexToFetch) {
          yymmIndexList = requestAdherence.yymmIndexToFetch
        }

        yymmIndexList = yymmIndexList.filter((yymmIndex) => {
          return !projectDataDigest?.coveredYYMMIndex.includes(yymmIndex)
        })
        // console.log(`yymmIndexList to Fetch: ${yymmIndexList}`)

        const needFetchData = yymmIndexList.length > 0
        // console.log(`needFetchAdherenceData: ${needFetchData}`)

        if (needFetchData) {
          // task data fetch will inlcude all task digest so only need to do once
          // use coveredYYMMIndex to detemine whether task data has been fetched or not
          const requestTaskData = !projectDataDigest?.coveredYYMMIndex
          const requestScheduleTaskData = projectHasScheduleTask && requestTaskData

          doREQUEST_PROJECT_DATA_DIGEST({
            payload: {
              projectId,
              yymmIndexList: yymmIndexList,
              requestTaskData,
              requestScheduleTaskData,
              requestGarminData: garminConnectEnabled || garminDeviceEnable,
              requestDexcomData: dexcomDataEnabled,
              garminTaskId: garminDeviceTask?.id,
            },
          })
        }
      }
    }
  }, [requestAdherence, method.id, projectAdherence?.participantAdherence])

  useEffect(() => {
    if (requestGarminDirectBbiDigest) {
      const {yymmddIndexStart, yymmddIndexEnd} = requestGarminDirectBbiDigest

      let requestYymmddIndexList: number[] = t.toYYMMDDListFromRange(yymmddIndexStart, yymmddIndexEnd)

      if (garminDirectDataDigest?.coveredYymmddIndexSet) {
        requestYymmddIndexList = requestYymmddIndexList.filter((yymmddIndex) => {
          return !garminDirectDataDigest?.coveredYymmddIndexSet?.has(yymmddIndex)
        })
      }

      if (requestYymmddIndexList.length) {
        doREQUEST_PROJECT_GARMIN_BBI_DATA_DIGEST_FETCH({
          payload: {
            projectId,
            yymmddIndexList: requestYymmddIndexList,
          },
        })
      }
    }
  }, [requestGarminDirectBbiDigest])

  useEffect(() => {
    if (requestGarminConnectWearTime) {
      const {yymmddIndexStart, yymmddIndexEnd} = requestGarminConnectWearTime
      let requestYymmddIndexList: number[] = t.toYYMMDDListFromRange(yymmddIndexStart, yymmddIndexEnd)

      if (garminConnectDataDigest?.coveredYymmddIndexSet) {
        requestYymmddIndexList = requestYymmddIndexList.filter((yymmddIndex) => {
          return !garminConnectDataDigest?.coveredYymmddIndexSet?.has(yymmddIndex)
        })
      }

      if (requestYymmddIndexList.length) {
        doREQUEST_PROJECT_GARMIN_CONNECT_WEAR_TIME_DATA_FETCH({
          payload: {
            projectId,
            yymmddIndexList: requestYymmddIndexList,
          },
        })
      }
    }
  }, [requestGarminConnectWearTime])

  useEffect(() => {
    if (requestParticipantIssues) {
      doREQUEST_PROJECT_PARTICIPANT_ISSUES_FETCH({
        payload: {
          projectId,
        },
      })
    }
  }, [requestParticipantIssues])
}

const convertDateToYYMMIndex = (date: Date): number => {
  const year = date.getUTCFullYear() % 100 // take 2 digits
  const month = date.getUTCMonth() + 1
  return year * 100 + month
}
